// src/store/userStore.js
import { reactive, computed } from 'vue';
import apiClient from '@/mechanism/api-client';
import { Storage } from '@/mechanism/storage';
import { URL_CONFIG } from "@/url_config";
import { triggerLogoutHooks } from '@/mechanism/appHooks';
import { CookieEngine } from '@/mechanism/cookie-engine';

const extractUserId = (info) => {
    return info?.id || info?.user_id || info?.uid || null;
};

// 🌟 配置缓存有效期 (例如：12 小时 = 12 * 60 * 60 * 1000 毫秒)
const CACHE_TTL = 24 * 60 * 60 * 1000 * 7;

export const userStore = reactive({
    userInfo: null,
    isLoading: false,

    avatar: computed(() => {
        const url = userStore.userInfo?.avatar;
        return url ? url : 'https://oss.toolup.cn/file/app/default_avatar.png';
    }),

    async initUser() {
        try {
            // 1. 同时读取本地数据和最后更新的时间戳
            const localInfo = await Storage.get('user_info');
            const lastUpdateTime = await Storage.get('user_info_timestamp');

            const now = Date.now();
            // 校验逻辑：数据存在 且 时间戳存在 且 未超过有效期
            const isCacheValid = localInfo && lastUpdateTime && (now - lastUpdateTime < CACHE_TTL);

            if (isCacheValid) {
                console.log("[userStore] 命中本地未过期缓存，跳过网络请求");
                this.userInfo = localInfo;
                await Storage.setUserId(extractUserId(this.userInfo));

                // 🌟 核心：缓存有效，直接中断执行，不再发起远程获取
                return;
            }

            console.log("[userStore] 缓存已过期或不存在，准备请求远端...");

            // 体验优化：虽然过期了，但如果在请求回来之前不想让页面白屏，可以先用过期数据兜底 UI
            if (localInfo) {
                this.userInfo = localInfo;
                await Storage.setUserId(extractUserId(this.userInfo));
            }

        } catch (e) {
            console.error('[userStore] 读取或校验本地缓存失败，准备重新获取', e);
            this.userInfo = null;
            await Storage.setUserId(null);
        }

        // 2. 缓存失效或校验失败，正常发起远端请求
        await this.fetchUserInfo();
    },

    async fetchUserInfo() {
        this.isLoading = true;
        try {
            const res = await apiClient.get(`${URL_CONFIG.account_url}/account/api/user/info`);
            if (res.data && res.data.status === 'ok') {
                this.userInfo = res.data.data;

                // 🌟 获取成功后：更新状态、更新用户信息、并写入最新时间戳
                await Storage.setUserId(extractUserId(this.userInfo));
                await Storage.set('user_info', this.userInfo);
                await Storage.set('user_info_timestamp', Date.now());

                console.log("[userStore] 远端获取用户信息成功并已缓存");
            }
        } catch (error) {
            console.error('[userStore] 获取远端用户信息失败', error);
        } finally {
            this.isLoading = false;
        }
    },

    async logout() {
        // 先触发退出钩子（此时请求头还带着有效的 access_token，后端鉴权可以通过）
        await triggerLogoutHooks();

        this.userInfo = null;

        await Storage.setUserId(null);

        await Storage.set('access_token', null);
        await Storage.set('user_info', null);
        // 🌟 登出时别忘了清理时间戳
        await Storage.set('user_info_timestamp', null);
        await Storage.set('login_method', null);

        await CookieEngine.clearAllAppCookies();
    }
});